import { noop } from "lodash";
import { memo, useMemo } from "react";

import type { TaxCode } from "@/apis/gql/types";
import {
  TaxCodeForm,
  type FormValues as TaxCodeFormValues,
} from "@/components/shared/form/lineItems/taxCode";
import { SpecialAccountBU } from "@/components/shared/tables/automatic-account-bu";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useGetAllAccounts } from "@/queries/accounts/get-all";
import { useGetAllTaxCodes } from "@/queries/tax-codes/get-all";

export enum SpecialFilterValue {
  Unknown = "unknown",
}

type Props = {
  lineItemIds: string[];
  accountIds: string[];
  taxCodeIds: (string | null)[];
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  onChange: (value: TaxCodeFormValues) => Promise<void> | void;
  onError?: (error: Error) => void;
  onSuccess?: (value: string | null) => Promise<void> | void;
  tooltipDescriptions?: {
    id: string;
    name: string;
    justification: string;
  }[];
  readOnly?: boolean | string | React.ReactNode;
  disabled?: boolean | string | React.ReactNode;
};

type TaxCodeExplanationProps = {
  taxCodes: { id: string; name: string; justification: string }[];
};

const TaxCodeExplanation = ({ taxCodes }: TaxCodeExplanationProps) => {
  return (
    <div className="tooltip-maxwidth whitespace-normal text-wrap">
      {taxCodes.map(({ id, name, justification }) => (
        <div key={id} style={{ marginBottom: "8px" }}>
          <b>
            {id} - {name}
          </b>
          <br />
          <span>{justification}</span>
        </div>
      ))}
    </div>
  );
};

type MultipleTaxCodesProps = {
  taxCodes: (TaxCode | undefined)[];
  tooltipDescriptions: {
    id: string;
    name: string;
    justification: string;
  }[];
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
};

export const MultipleTaxCodes = ({
  taxCodes,
  className,
  tooltipDescriptions,
}: MultipleTaxCodesProps) => {
  const taxCodeNames = taxCodes.map((a) => a?.name).join(", ");
  const taxCodeIds = taxCodes.map((a) => a?.datevBu).join(", ");

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild className={cn("rounded", className)}>
          <Button
            variant="ghost"
            className={cn(
              "items-center whitespace-nowrap flex justify-between w-full overflow-hidden p-2 h-11",
            )}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="flex flex-col overflow-hidden">
              <span className="text-ellipsis overflow-hidden">
                {taxCodeNames}
              </span>
              <span className="text-gray-500 text-xs text-left text-ellipsis overflow-hidden">
                {taxCodeIds}
              </span>
            </div>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <TaxCodeExplanation taxCodes={tooltipDescriptions} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

type SingleTaxCodeProps = {
  taxCodes: TaxCode[];
  lineItemIds: string[];
  selectedTaxCodes: (TaxCode | undefined)[];
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  onChange: (value: TaxCodeFormValues) => Promise<void> | void;
  onError: (error: Error) => void;
  onSuccess: (value: string | null) => Promise<void> | void;
  readOnly?: boolean | string | React.ReactNode;
  disabled?: boolean | string | React.ReactNode;
  tooltipDescriptions: {
    id: string;
    name: string;
    justification: string;
  }[];
};

export const SingleTaxCode = ({
  taxCodes,
  readOnly,
  disabled,
  className,
  lineItemIds,
  onChange,
  onError,
  onSuccess,
  tooltipDescriptions,
  selectedTaxCodes,
}: SingleTaxCodeProps) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger className="w-full">
          <TaxCodeForm
            readOnly={readOnly}
            initialValues={{
              taxCodeId: selectedTaxCodes[0]?.taxCodeId ?? null,
              lineItemIds,
              buJustification: tooltipDescriptions[0]?.justification,
            }}
            taxCodes={taxCodes}
            onSubmit={onChange}
            onError={onError}
            onSuccess={onSuccess}
            fullWidth={false}
            variant="ghost"
            className={className}
            disabled={disabled}
          />
        </TooltipTrigger>
        <TooltipContent>
          <TaxCodeExplanation taxCodes={tooltipDescriptions} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const TaxCodeCell = memo(
  ({
    readOnly = false,
    accountIds,
    lineItemIds,
    taxCodeIds,
    onChange = noop,
    onError = noop,
    onSuccess = noop,
    tooltipDescriptions = [],
    className,
    disabled = false,
  }: Props) => {
    const { taxCodes } = useGetAllTaxCodes();
    const { accounts } = useGetAllAccounts();

    const currentTaxCodes = useMemo(() => {
      if (!taxCodes) return [];

      return taxCodeIds.map((taxCodeId) =>
        taxCodes.find((a) => a.taxCodeId === taxCodeId),
      );
    }, [taxCodeIds, taxCodes]);

    if (currentTaxCodes.length > 1) {
      return (
        <MultipleTaxCodes
          taxCodes={currentTaxCodes}
          tooltipDescriptions={tooltipDescriptions}
          className={className}
        />
      );
    }

    const [singleAccountId] = accountIds;

    const account = accounts?.find((a) => a.accountId === singleAccountId);

    if (account?.automatic) {
      return (
        <SpecialAccountBU
          label="Automatischer"
          subLabel="Steuerschlüssel"
          className={cn(className, Boolean(disabled) && "opacity-50")}
        />
      );
    }

    if (account?.buExport === false) {
      return (
        <SpecialAccountBU
          label="Konto ohne BU"
          className={cn(Boolean(disabled) && "opacity-50")}
        />
      );
    }

    return (
      <SingleTaxCode
        taxCodes={taxCodes ?? []}
        lineItemIds={lineItemIds}
        selectedTaxCodes={currentTaxCodes}
        onChange={onChange}
        onError={onError}
        onSuccess={onSuccess}
        tooltipDescriptions={tooltipDescriptions}
        className={className}
        readOnly={readOnly}
        disabled={disabled}
      />
    );
  },
);
